<template>
  <div>
    <app-detail-header
      :show-toggle="true"
      :show-delete="false"
      :show-save="false"
      :show-cancel="false"
      @appDetailHeaderButtonClicked="onHeaderButtonClicked" />
    <div class="columns is-gapless">
      <div class="column is-narrow">
        <vehicle-list-side-menu
          v-model="filter"
          @filter="filterList"
          @reset="resetList" />
      </div>
      <div class="column">
        <div class="tile is-parent">
          <article class="tile is-child box">
            <bulma-table
              class="table is-bordered is-striped is-narrow is-fullwidth"
              :columns="columns"
              :page-index="filter.pageIndex"
              :page-size="filter.pageSize"
              :total-rows="totalRows"
              :is-loading="isTableLoading"
              :sort-column="filter.sortColumn"
              :sort-order="filter.sortOrder"
              @pageChanged="onPageChange"
              @sort="sort">
              <tr
                v-for="(c, idx) in entitySummaries"
                :class="{ 'is-selected' : selectedRow === idx }"
                @click="highlightSelected(idx, $event)"
                :key="c.id">
                <td>
                  <router-link
                    :to="{ path: c.vehicleID }"
                    append>
                    {{ c.rego }}
                  </router-link>
                </td>
                <td>{{ `${c.make.trim()} ${c.model.trim()}` }}</td>
                <td>{{ c.customerName }}</td>
                <td>
                  <span v-if="c.buildMonth && c.buildYear">{{ $filters.pad(c.buildMonth, 2) }}/{{ c.buildYear }}</span>
                </td>
                <td>{{ c.bodyStyle }}</td>
                <td class="has-text-centered">{{ c.paintGroup }}</td>
                <td class="has-text-centered">{{ c.transmissionType }}</td>
                <td class="has-text-centered">
                  <span
                    class="icon"
                    :class="[ c.active ? 'has-text-success' : 'has-text-danger' ]">
                    <i
                      class="mdi mdi-24px"
                      :class="[ c.active ? 'mdi-check' : 'mdi-close' ]" />
                  </span>
                </td>
                <td>
                  <router-link
                    class="button is-primary is-small is-inverted"
                    :to="{ path: c.vehicleID }"
                    append>
                    <span class="icon is-medium">
                      <i class="mdi mdi-lead-pencil mdi-24px" />
                    </span>
                  </router-link>
                </td>
              </tr>
              <template slot="empty">
                <section class="section">
                  <div class="content has-text-grey has-text-centered">
                    <span icon="icon is-large">
                      <i class="mdi mdi-48px mdi-emoticon-sad" />
                    </span>
                    <p>Nothing</p>
                  </div>
                </section>
              </template>
            </bulma-table>
          </article>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import AppDetailHeader from '@/components/AppDetailHeader'
import VehicleService from './VehicleService'
import VehicleListSideMenu from './VehicleListSideMenu'
import { EventHubTypes, AppHeaderButtonTypes } from '@/enums'
import BulmaTable from '@/components/BulmaTable'
import Columns from './columns'
import { NumberFiltersMixin } from '@/components/mixins/filters'
import ProtoBuf from 'protobufjs'
import StoreMixin from './storeMixin'
import PrintPreviewRoutes from '@/components/printpreview/route-types'
import { KeyValuePairModel } from '@/classes/viewmodels'
import _transform from 'lodash/transform'
import { Emailer } from '@/classes'

export default {
  name: 'VehicleListView',
  components: {
    AppDetailHeader,
    VehicleListSideMenu,
    BulmaTable
  },
  mixins: [StoreMixin, NumberFiltersMixin],
  data() {
    return {
      entitySummaries: {},
      totalRows: 0,
      filter: {
        rego: '',
        make: '',
        model: '',
        customer: '',
        active: 1,
        sortColumn: Columns[0].name,
        sortOrder: Columns[0].defaultOrder,
        pageIndex: 1,
        pageSize: 18
      },
      toggle: true,
      selectedRow: null,
      isTableLoading: false
    }
  },
  computed: {
    filterKey() {
      if (this.$userInfo) {
        return `${this.$userInfo.sessionId}|${this.$route.meta.fkey}`
      } else {
        return ''
      }
    },
    columns() {
      return Columns
    }
  },
  async created() {
    await this.getData()
    this.$eventHub.$on(EventHubTypes.ToggleSideMenu, data => {
      this.toggle = false
      setTimeout(() => {
        this.toggle = true
      }, 200)
    })
  },
  methods: {
    highlightSelected(index, event) {
      this.selectedRow = index
    },
    onHeaderButtonClicked(action) {
      switch (action) {
        case AppHeaderButtonTypes.AddNew:
          this.addNew()
          break
        case AppHeaderButtonTypes.Print:
          this.print()
          break
        case AppHeaderButtonTypes.Delete:
          break
        case AppHeaderButtonTypes.Save:
          break
        case AppHeaderButtonTypes.Cancel:
          break
      }
    },
    filterList() {
      this.filter.pageIndex = 1
      this.persistFilter()
      this.getData()
    },
    sort(name, order) {
      this.filter.sortColumn = name
      this.filter.sortOrder = order
      this.persistFilter()
      this.getData()
    },
    resetList() {
      this.filter = {
        rego: '',
        make: '',
        model: '',
        customer: '',
        active: 1,
        sortColumn: Columns[0].name,
        sortOrder: Columns[0].defaultOrder,
        pageIndex: 1,
        pageSize: 18
      }
      this.persistFilter()
      this.getData()
    },
    persistFilter() {
      sessionStorage.setItem(this.filterKey, JSON.stringify(this.filter))
    },
    retrieveFilter() {
      const filter = JSON.parse(sessionStorage.getItem(this.filterKey))
      if (filter) {
        this.filter = filter
      }
    },
    serialiseFilter() {
      const entityProto = require('../../assets/proto/vehicle/VehicleFilter.proto')
      const filterMessage = entityProto.VehicleFilter.VehicleFilter
      const buffer = filterMessage.encode(this.filter).finish()
      const b64encoded = ProtoBuf.util.base64.encode(buffer, 0, buffer.length)
      return b64encoded
    },
    onPageChange(pageIndex) {
      this.filter.pageIndex = pageIndex
      this.persistFilter()
      this.getData()
    },
    async getData() {
      this.isTableLoading = true

      this.retrieveFilter()
      this.entitySummaries = await VehicleService.getEntitySummaries(this.serialiseFilter())
      this.totalRows = this.entitySummaries.length > 0 ? this.entitySummaries[0].totalRows : 0

      this.isTableLoading = false
    },
    addNew() {
      this.addStoreItem()
    },
    print() {
      let params = Object.assign(
        {
          companyId: this.$userInfo.companyId
        },
        this.filter
      )
      params.sortColumn = this.filter.sortColumn.toUpperCase()
      params.pageIndex = 1
      params.pageSize = this.totalRows
      params = _transform(params, function(result, value, key) {
        result[key.toLowerCase()
        .replace('rego', 'regonumber')
        .replace('sortorder', 'sortdirection')] = value
      })

      const keyValuePairs = KeyValuePairModel.convertToKeyValuePairs(params)
      this.addReportParameters(keyValuePairs)

      const emailer = new Emailer()
      emailer.subject = 'Vehicle List Report'
      emailer.reportName = 'rptTablesCar'
      this.addEmailer(emailer)
      this.$router.push({
        name: PrintPreviewRoutes.PrintPreview.name,
        params: { reportName: this.$route.meta.report },
        query: { parameterId: this.$guid.newGuid(), emailerId: emailer.id }
      })
    }
  }
}
</script>
